import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight } from "lucide-react";
import BackgroundVideo from "@/components/BackgroundVideo";
import KineticText from "@/components/KineticText";
import SiteFooter from "@/components/SiteFooter";

const fadeUp = {
  hidden: { opacity: 0, y: 36 },
  show: { opacity: 1, y: 0, transition: { duration: 0.85, ease: [0.16, 1, 0.3, 1] } },
};

const About = () => (
  <motion.div
    data-testid="about-page"
    initial={{ opacity: 0, y: 30 }}
    animate={{ opacity: 1, y: 0 }}
    exit={{ opacity: 0 }}
    transition={{ duration: 0.5 }}
    className="relative"
  >
    <BackgroundVideo />
    <div className="grain-overlay" />

    <header className="relative z-10 flex items-center justify-between px-8 md:px-16 lg:px-24 pt-8 md:pt-10">
      <Link to="/" data-testid="about-logo-link">
        <img src="/assets/oneskin-logo.png" alt="One Skin" className="w-24 md:w-28" />
      </Link>
      <Link
        to="/"
        data-testid="about-back-link"
        className="inline-flex items-center gap-2 text-xs tracking-[0.25em] uppercase text-[#3D6B55] hover:text-[#1E3A2F] transition-colors duration-300"
      >
        <ArrowLeft className="w-3.5 h-3.5" /> Back
      </Link>
    </header>

    <section className="relative z-10 min-h-[70vh] flex items-center px-8 md:px-16 lg:px-24 py-20">
      <div className="max-w-4xl">
        <KineticText
          testIdPrefix="about-line"
          lines={[
            {
              text: "SKINCARE,",
              className:
                "font-heading font-black text-[#1E3A2F] text-4xl sm:text-6xl lg:text-8xl leading-[1.02] tracking-tight",
            },
            {
              text: "WITHOUT THE NOISE.",
              className:
                "font-heading font-black italic text-[#8B1A2A] text-4xl sm:text-6xl lg:text-8xl leading-[1.02] tracking-tight",
            },
          ]}
        />
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7, duration: 1, ease: [0.16, 1, 0.3, 1] }}
          className="mt-12 space-y-6 max-w-2xl"
        >
          <p className="font-heading font-bold text-[#1E3A2F] text-xl md:text-2xl" data-testid="about-lead">
            Your skin doesn&#8217;t need another trend.
          </p>
          <p className="text-base md:text-lg leading-relaxed text-[#3D6B55] font-medium">
            It needs formulas that make sense, ingredients that do their job, and a routine you can
            actually stick to.
          </p>
          <p className="text-base md:text-lg leading-relaxed text-[#3D6B55] font-medium">
            One Skin is functional skincare built around real skin concerns — thoughtfully
            formulated, evidence-led and made for everyday Indian skin.
          </p>
          <div className="pt-6">
            <Link to="/early-access">
              <motion.button
                data-testid="about-cta-btn"
                whileHover={{ y: -4 }}
                whileTap={{ scale: 0.97 }}
                className="group inline-flex items-center gap-3 bg-[#1E3A2F] text-[#F5EFE6] px-9 py-4 rounded-full font-bold uppercase tracking-[0.15em] text-sm hover:bg-[#3D6B55] transition-colors duration-300 shadow-[0_18px_40px_-16px_rgba(30,58,47,0.5)]"
              >
                Be first in line
                <ArrowRight
                  className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1.5"
                  strokeWidth={2.5}
                />
              </motion.button>
            </Link>
          </div>
        </motion.div>
      </div>
    </section>

    <SiteFooter />
  </motion.div>
);

export default About;
